import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ProductCard from "../components/ProductCard";
import { productService, categoryService } from "../services/api";

const cn = (...cls) => cls.filter(Boolean).join(" ");

const PER_PAGE = 12;

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const [sortBy, setSortBy] = useState("latest");

  // URL-driven category id
  const categoryIdFromUrl = useMemo(() => {
    const raw = searchParams.get("category");
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [searchParams]);

  // URL-driven page (default 1)
  const pageFromUrl = useMemo(() => {
    const raw = Number(searchParams.get("page") || 1);
    return Number.isFinite(raw) && raw > 0 ? raw : 1;
  }, [searchParams]);

  const selectedCategory = useMemo(() => {
    if (!categoryIdFromUrl) return null;
    return categories.find((c) => Number(c.id) === Number(categoryIdFromUrl)) || null;
  }, [categories, categoryIdFromUrl]);

  // meta for pagination (Laravel usually returns meta.current_page & meta.last_page)
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });

  const extractArray = (res) => {
    const data = res?.data ?? res;
    if (Array.isArray(data)) return { items: data, meta: null };
    if (data && Array.isArray(data.data)) return { items: data.data, meta: data.meta || data.pagination || null };
    return { items: [], meta: null };
  };

  // Load categories once
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await categoryService.getCategories();
        const categoryData = response.data || response;
        setCategories(Array.isArray(categoryData) ? categoryData : []);
      } catch {
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  // If URL has category that doesn't exist => clear it (after categories loaded)
  useEffect(() => {
    if (loadingCategories) return;
    if (!categoryIdFromUrl) return;

    const exists = categories.some((c) => Number(c.id) === Number(categoryIdFromUrl));
    if (!exists) setSearchParams({});
  }, [categories, categoryIdFromUrl, loadingCategories, setSearchParams]);

  // Ensure page is reset when category or sort changes
  useEffect(() => {
    // keep category, reset page
    const params = new URLSearchParams(searchParams);
    params.set("page", "1");
    setSearchParams(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdFromUrl, sortBy]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoadingProducts(true);

      const params = {
        sort: sortBy === "latest" ? "-created_at" : sortBy,
        page: pageFromUrl,
        limit: PER_PAGE,
      };
      if (categoryIdFromUrl) params.category_id = categoryIdFromUrl;

      const res = await productService.getProducts(params);
      const { items, meta: serverMeta } = extractArray(res);

      setProducts(items);

      if (serverMeta?.current_page && serverMeta?.last_page) {
        setMeta({
          current_page: Number(serverMeta.current_page) || pageFromUrl,
          last_page: Number(serverMeta.last_page) || pageFromUrl,
          total: Number(serverMeta.total) || 0,
        });
      } else {
        // fallback if backend doesn’t send meta
        setMeta({
          current_page: pageFromUrl,
          last_page: items.length === PER_PAGE ? pageFromUrl + 1 : pageFromUrl,
          total: 0,
        });
      }
    } catch {
      setProducts([]);
      setMeta({ current_page: pageFromUrl, last_page: pageFromUrl, total: 0 });
    } finally {
      setLoadingProducts(false);
    }
  }, [categoryIdFromUrl, pageFromUrl, sortBy]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleCategoryClick = (cat) => {
    setSearchParams({ category: String(cat.id), page: "1" });
  };

  const handleShowAll = () => {
    setSearchParams({ page: "1" });
  };

  const goToPage = (nextPage) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", String(nextPage));
    setSearchParams(params);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  };

  const getPageTitle = () =>
    selectedCategory ? `${selectedCategory.name} - Boutique Luxe | ORIMA Maroc` : "Boutique en Ligne - Accessoires de Luxe | ORIMA Maroc";

  const resultsText = useMemo(() => {
    if (loadingProducts) return "Chargement…";
    const p = meta?.current_page || pageFromUrl;
    const l = meta?.last_page || 1;
    if (selectedCategory) return `${products.length} produits — ${selectedCategory.name} (page ${p}/${l})`;
    return `${products.length} produits (page ${p}/${l})`;
  }, [loadingProducts, meta, pageFromUrl, products.length, selectedCategory]);

  const currentPage = meta?.current_page || pageFromUrl;
  const lastPage = meta?.last_page || 1;

  return (
    <>
      <Helmet>
        <title>{getPageTitle()}</title>
        <meta
          name="description"
          content={
            selectedCategory
              ? `Découvrez notre collection ${selectedCategory.name} sur ORIMA. Livraison gratuite au Maroc en 1-2 jours.`
              : `Explorez notre boutique ORIMA: sacs, accessoires et montres premium. Livraison gratuite partout au Maroc.`
          }
        />
        <link
          rel="canonical"
          href={
            selectedCategory
              ? `https://yoursite.com/shop?category=${selectedCategory.id}&page=${currentPage}`
              : `https://yoursite.com/shop?page=${currentPage}`
          }
        />
      </Helmet>

      <div className="bg-white min-h-screen" style={{ fontFamily: "DM Sans, sans-serif" }}>
        {/* HERO */}
        <div className="relative h-[260px] sm:h-[320px] overflow-hidden">
          <img
            src="https://www.harpersbazaararabia.com/cloud/2021/09/15/j2jOA42H-Handbag-Trends-Autumn-2020-Lead-2.jpg"
            className="w-full h-full object-cover"
            alt="Boutique ORIMA"
          />
          <div className="absolute inset-0 bg-black/35" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4 text-center">
            <h1 className="text-3xl sm:text-4xl font-light tracking-wide">
              {selectedCategory ? selectedCategory.name.toUpperCase() : "BOUTIQUE"}
            </h1>
            <p className="mt-3 text-sm sm:text-base text-white/90 max-w-xl">
              Sacs, montres et accessoires premium • Livraison gratuite au Maroc
            </p>
          </div>
        </div>

        {/* Categories bar */}
        <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-3 overflow-x-auto whitespace-nowrap hide-scrollbar">
              <button
                onClick={handleShowAll}
                className={cn(
                  "px-4 py-2 rounded-full border text-sm font-semibold transition-all flex-shrink-0",
                  !categoryIdFromUrl ? "bg-yellow-200 border-yellow-300 text-gray-900" : "bg-white border-gray-200 text-gray-700 hover:border-gray-400"
                )}
              >
                Tous
              </button>

              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat)}
                  className={cn(
                    "px-4 py-2 rounded-full border text-sm font-semibold transition-all flex-shrink-0",
                    Number(categoryIdFromUrl) === Number(cat.id)
                      ? "bg-yellow-200 border-yellow-300 text-gray-900"
                      : "bg-white border-gray-200 text-gray-700 hover:border-gray-400"
                  )}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-sm text-gray-700">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-semibold">{resultsText}</span>

                {selectedCategory && (
                  <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs">
                    Catégorie: {selectedCategory.name}
                    <button onClick={handleShowAll} className="hover:text-gray-900 font-bold">×</button>
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <span className="text-gray-500 hidden sm:block">Trier:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 px-3 py-2 text-sm rounded-xl bg-white"
                >
                  <option value="latest">Plus récent</option>
                  <option value="price_asc">Prix: du + bas au + haut</option>
                  <option value="price_desc">Prix: du + haut au + bas</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Grid + Pagination */}
        <div className="max-w-7xl mx-auto px-4 py-10">
          {loadingProducts ? (
            <div className="text-center py-20 text-gray-500">Chargement des produits…</div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 text-gray-500">Aucun produit trouvé.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 sm:gap-x-8 gap-y-10 sm:gap-y-14">
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>

              {/* Pagination */}
              <div className="mt-10 flex items-center justify-between gap-4">
                <button
                  onClick={() => goToPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className={cn(
                    "px-4 py-2 rounded-xl border text-sm font-semibold transition",
                    currentPage <= 1 ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-gray-300 text-gray-700 hover:border-gray-400"
                  )}
                >
                  ← Précédent
                </button>

                <div className="text-sm text-gray-600">
                  Page <span className="font-semibold text-gray-900">{currentPage}</span> /{" "}
                  <span className="font-semibold text-gray-900">{lastPage}</span>
                </div>

                <button
                  onClick={() => goToPage(Math.min(lastPage, currentPage + 1))}
                  disabled={currentPage >= lastPage}
                  className={cn(
                    "px-4 py-2 rounded-xl border text-sm font-semibold transition",
                    currentPage >= lastPage ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-gray-300 text-gray-700 hover:border-gray-400"
                  )}
                >
                  Suivant →
                </button>
              </div>
            </>
          )}
        </div>

        <style>{`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>
      </div>
    </>
  );
};

export default Shop;
